import { AddressAutofill } from '@mapbox/search-js-react';
import { CountrySelect, Input, RegionSelect } from '@retainerclub/common';
import { useFormValues, useReferral } from '@retainerclub/hooks';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

function OnboardingFormAddress() {
  const { setValue } = useFormContext();
  const [sameAsBillingAddress, setSameAsBillingAddress] = useState(false);
  const { billingAddress } = useFormValues();
  const { referral } = useReferral();

  function handleCheckboxChange({ target: { checked } }) {
    setSameAsBillingAddress(checked);

    if (checked) {
      setValue('accountAddressesAttributes[0].address', billingAddress.address);
      setValue(
        'accountAddressesAttributes[0].address2',
        billingAddress.address2,
      );
      setValue('accountAddressesAttributes[0].city', billingAddress.city);
      setValue('accountAddressesAttributes[0].region', billingAddress.region);
      setValue(
        'accountAddressesAttributes[0].postalCode',
        billingAddress.postalCode,
      );
      setValue('accountAddressesAttributes[0].country', billingAddress.country);
    }

    if (!checked) {
      setValue('accountAddressesAttributes[0].address', null);
      setValue('accountAddressesAttributes[0].address2', null);
      setValue('accountAddressesAttributes[0].city', null);
      setValue('accountAddressesAttributes[0].region', null);
      setValue('accountAddressesAttributes[0].postalCode', null);
      setValue('accountAddressesAttributes[0].country', 'US');
    }
  }

  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-6">
      {referral?.isFreeTrial && (
        <div className="relative mb-2 flex gap-x-3 sm:col-span-6">
          <div className="flex h-6 items-center">
            <input
              type="checkbox"
              checked={sameAsBillingAddress}
              onChange={handleCheckboxChange}
              className="size-5 rounded-sm border-rcgray-600 text-rcprimary-400 focus:ring-rcprimary-400 disabled:cursor-not-allowed disabled:bg-rcgray-600 disabled:text-rcgray-600 disabled:ring-rcgray-700"
            />
          </div>
          <div className="text-sm leading-6">
            <label
              htmlFor="comments"
              className="font-medium text-black/90 text-md"
            >
              Same as Billing Address?
            </label>
            <p className="text-black/60 text-xs">
              Select to copy the billing address to the shipping address.
            </p>
          </div>
        </div>
      )}
      <div className="sm:col-span-6">
        <AddressAutofill
          accessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
          confirmOnBrowserAutofill={{
            minimap: false,
            skipConfirmModal: (feature) =>
              ['exact', 'high'].includes(
                feature.properties.match_code.confidence,
              ),
          }}
          onRetrieve={(address) => {
            if (address?.features?.length > 0) {
              const {
                country_code: countryCode,
                region_code: regionCode,
                matching_name: matchingName,
              } = address.features[0].properties;

              setValue(
                'accountAddressesAttributes[0].country',
                countryCode.toUpperCase(),
              );

              setValue(
                'accountAddressesAttributes[0].region',
                regionCode.toUpperCase(),
              );

              setValue('accountAddressesAttributes[0].address', matchingName);
            }
          }}
          onChange={(event) => {
            setValue('accountAddressesAttributes[0].address', event);
          }}
          options={{
            country: 'US',
            language: 'en',
            proximity: 'ip',
          }}
        >
          <Input
            name="accountAddressesAttributes[0].address"
            label="Address"
            required
            autoComplete=""
          />
        </AddressAutofill>
      </div>

      <div className="sm:col-span-3">
        <Input
          name="accountAddressesAttributes[0].address2"
          label="Address Line 2"
          autoComplete="address-line2"
        />
        <p className="mt-1 text-rccallouts-warning text-xs">
          Remember to add Unit, Suite, or Apt number to avoid delays.
        </p>
      </div>
      <div className="sm:col-span-3">
        <Input
          name="accountAddressesAttributes[0].city"
          label="City"
          required
          autoComplete="address-level2"
        />
      </div>
      <div className="sm:col-span-2">
        <CountrySelect
          name="accountAddressesAttributes[0].country"
          required
          autoComplete=""
          usOnly
        />
      </div>
      <div className="sm:col-span-2">
        <RegionSelect
          name="accountAddressesAttributes[0].region"
          countryFieldName="accountAddressesAttributes[0].country"
          autoComplete=""
          required
        />
      </div>
      <div className="sm:col-span-2">
        <Input
          name="accountAddressesAttributes[0].postalCode"
          label="Postal Code"
          required
          autoComplete="postal-code"
        />
      </div>
    </div>
  );
}

export default OnboardingFormAddress;
